/**
 * VideoTile — thin coordinator that picks between LiveTile (HLS from ONVIF)
 * and RecordedTile (day-slot MP4 blob playback). Adds:
 *   - REC / LIVE / clock overlays
 *   - Snapshot button (canvas → PNG download) — expanded view only
 *   - 24hr virtual timeline scrubber — expanded view only, recorded mode only
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Radio, Camera as SnapIcon, VideoOff, PowerOff, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, Bookmark } from "lucide-react";
import LiveTile from "@/components/LiveTile";
import RecordedTile from "@/components/RecordedTile";
import { toast } from "sonner";
import api from "@/lib/api";
import { istTimeStr, istDateStr, istStampFilename, istDayFraction, istParts, istISODateStr } from "@/lib/time";
import { useAuth } from "@/context/AuthContext";
import { saveSnapshot, captureFromVideo } from "@/lib/gallery";

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}
function pad(n) { return String(n).padStart(2, "0"); }

export default function VideoTile({ camera, expanded = false, onExpand, playbackMode = false, playbackDate = null }) {
  const [footageInfo, setFootageInfo] = useState(null);
  const [footageLoaded, setFootageLoaded] = useState(false);
  const tileRef = useRef(null);
  const now = useClock();
  const { user } = useAuth();

  // Digital PTZ — client-side CSS transform. Only usable in expanded view.
  const [ptz, setPtz] = useState({ x: 0, y: 0, zoom: 1 });
  // PTZ panel visibility. Auto-collapse on mobile so controls don't cover the video.
  const [ptzOpen, setPtzOpen] = useState(() => (typeof window !== "undefined" ? window.innerWidth >= 768 : true));
  const clampPtz = useCallback((next) => {
    const zoom = Math.min(4, Math.max(1, next.zoom));
    // when zoom == 1, no pan allowed (would show black). Else clamp so video edges stay in view.
    const maxPan = (zoom - 1) * 50; // percent
    const x = Math.min(maxPan, Math.max(-maxPan, next.x));
    const y = Math.min(maxPan, Math.max(-maxPan, next.y));
    return { x, y, zoom };
  }, []);
  const ptzStep = 12; // percent per click
  const zoomStep = 1.25;
  // Playback mode only — speed selector state. Live view keeps illusion intact.
  const [speed, setSpeed] = useState(1);
  useEffect(() => {
    const v = tileRef.current?.videoEl?.();
    if (v && !isNaN(v.playbackRate)) v.playbackRate = playbackMode ? speed : 1;
  }, [speed, playbackMode, footageInfo]);
  const ptzMove = useCallback((dx, dy) => setPtz((p) => clampPtz({ ...p, x: p.x + dx, y: p.y + dy })), [clampPtz]);
  const ptzZoom = useCallback((factor) => setPtz((p) => clampPtz({ ...p, zoom: p.zoom * factor })), [clampPtz]);
  const ptzHome = useCallback(() => setPtz({ x: 0, y: 0, zoom: 1 }), []);
  // Reset PTZ whenever expand toggles or camera changes
  useEffect(() => { setPtz({ x: 0, y: 0, zoom: 1 }); }, [expanded, camera.id]);

  // Rare, brief "signal loss" glitches to enhance real-CCTV feel (every 20-30 min, lasts 3-5 sec).
  // Deterministic-per-channel first fire time avoids synchronised glitches across tiles.
  const [glitching, setGlitching] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const scheduleNext = () => {
      // 20-30 min jitter, plus a small per-channel offset so tiles don't glitch together
      const base = 20 * 60 * 1000 + Math.random() * 10 * 60 * 1000;
      const offset = ((camera.channel || 1) * 37) % 90000;
      timer = setTimeout(() => {
        if (cancelled) return;
        setGlitching(true);
        const dur = 3000 + Math.random() * 2500; // 3-5.5s
        setTimeout(() => {
          if (cancelled) return;
          setGlitching(false);
          scheduleNext();
        }, dur);
      }, base + offset);
    };
    scheduleNext();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [camera.channel]);

  // Fetch today's footage assignment at the coordinator level so we can decide
  // WHICH sub-tile to render (recorded vs. live-fallback vs. camera-off).
  useEffect(() => {
    let mounted = true;
    setFootageLoaded(false);
    setFootageInfo(null);
    const load = () => api.get(`/cameras/${camera.id}/live`).then((r) => {
      if (!mounted) return;
      setFootageInfo(r.data.footage || null);
      setFootageLoaded(true);
    }).catch(() => { if (mounted) setFootageLoaded(true); });
    load();
    const t = setInterval(load, 60000); // day-slot could flip on the hour
    return () => { mounted = false; clearInterval(t); };
  }, [camera.id]);

  const hasFootage = !!footageInfo;
  const canLiveStream = camera.type === "onvif" && !!camera.rtsp_url;
  // Explicit live mode always wins. Otherwise:
  //  - recorded mode + footage → recorded
  //  - recorded mode + NO footage + ONVIF connected → auto-fallback to live
  //  - otherwise → camera-off placeholder
  const explicitLive = camera.display_mode === "live" && canLiveStream;
  const showLive = explicitLive || (!hasFootage && canLiveStream && camera.display_mode !== "live");
  const showRecorded = hasFootage && !explicitLive;
  const showOff = footageLoaded && !showLive && !showRecorded;

  const snapshot = useCallback((e) => {
    e.stopPropagation();
    const v = tileRef.current?.videoEl?.();
    if (!v || !v.videoWidth) {
      toast.error("No video frame available");
      return;
    }
    try {
      const line1 = `CH${String(camera.channel).padStart(2, "0")} · ${camera.name}`;
      const line2 = `${istDateStr()} ${istTimeStr()} IST`;
      const cap = captureFromVideo(v, [line1, line2]);
      if (!cap) { toast.error("Snapshot failed"); return; }
      const { fullCanvas, thumbUrl } = cap;

      // Persist thumbnail to gallery (per-tenant localStorage)
      const tenantId = user?.tenant_id || "default";
      const snapId = `${camera.id}_${Date.now()}`;
      saveSnapshot(tenantId, {
        id: snapId,
        cameraId: camera.id,
        channel: camera.channel,
        cameraName: camera.name,
        location: camera.location || "",
        timestamp: new Date().toISOString(),
        istDate: istDateStr(),
        istTime: istTimeStr(),
        thumb: thumbUrl,
      });

      // Trigger PNG download for evidence
      fullCanvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const safeName = camera.name.replace(/[^a-z0-9_-]+/gi, "_");
        a.href = url;
        a.download = `CH${String(camera.channel).padStart(2, "0")}_${safeName}_${istStampFilename()}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        toast.success("Snapshot saved + added to gallery");
      }, "image/png");
    } catch (_) {
      toast.error("Snapshot failed");
    }
  }, [camera, user]);

  // Seek recorded video to a specific IST wall-clock fraction of TODAY (0..1).
  // Uses the same (secondsSinceMidnight % duration) mapping as RecordedTile's initial
  // seek so playback stays deterministic and consistent with the "live" timestamp overlay.
  const seekFraction = useCallback((frac) => {
    const v = tileRef.current?.videoEl?.();
    if (!v || !v.duration || !Number.isFinite(v.duration)) return;
    const dur = v.duration;
    const secOfDay = Math.max(0, Math.min(86399, Math.floor(frac * 86400)));
    const pos = secOfDay % dur;
    try { v.currentTime = pos; } catch (_) { /* ignore */ }
  }, []);

  const timeStr = istTimeStr(now);
  const dateStr = istDateStr(now);
  const channel = `CH${String(camera.channel || 0).padStart(2, "0")}`;

  /* -------- Mobile touch gestures for digital PTZ --------
     - two-finger pinch → zoom in/out
     - two-finger drag (or one-finger drag when already zoomed) → pan
     Only active in expanded view where PTZ controls exist. */
  const touchStateRef = useRef({ mode: null, initialDist: 0, initialZoom: 1, lastX: 0, lastY: 0 });
  const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
  const midpoint = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });

  const onTouchStart = useCallback((e) => {
    if (!expanded || showOff) return;
    if (e.touches.length === 2) {
      touchStateRef.current = {
        mode: "pinch",
        initialDist: dist(e.touches[0], e.touches[1]),
        initialZoom: ptz.zoom,
        lastX: midpoint(e.touches[0], e.touches[1]).x,
        lastY: midpoint(e.touches[0], e.touches[1]).y,
      };
    } else if (e.touches.length === 1 && ptz.zoom > 1) {
      touchStateRef.current = { mode: "pan", initialDist: 0, initialZoom: ptz.zoom, lastX: e.touches[0].clientX, lastY: e.touches[0].clientY };
    } else {
      touchStateRef.current.mode = null;
    }
  }, [expanded, showOff, ptz.zoom]);

  const onTouchMove = useCallback((e) => {
    const st = touchStateRef.current;
    if (!st.mode) return;
    if (st.mode === "pinch" && e.touches.length === 2) {
      e.preventDefault();
      const d = dist(e.touches[0], e.touches[1]);
      const scaleFactor = d / (st.initialDist || 1);
      setPtz((p) => clampPtz({ ...p, zoom: st.initialZoom * scaleFactor }));
    } else if (st.mode === "pan" && e.touches.length === 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - st.lastX;
      const dy = e.touches[0].clientY - st.lastY;
      touchStateRef.current.lastX = e.touches[0].clientX;
      touchStateRef.current.lastY = e.touches[0].clientY;
      const rect = e.currentTarget.getBoundingClientRect();
      const dxPct = -(dx / rect.width) * 100;
      const dyPct = -(dy / rect.height) * 100;
      setPtz((p) => clampPtz({ ...p, x: p.x + dxPct, y: p.y + dyPct }));
    }
  }, [clampPtz]);

  const onTouchEnd = useCallback(() => {
    touchStateRef.current.mode = null;
  }, []);

  return (
    <div
      data-testid={`camera-tile-${camera.channel}`}
      className={`relative bg-black border border-zinc-900 overflow-hidden scanline group cursor-pointer flex flex-col ${expanded ? "w-full h-full min-h-0" : ""}`}
      onClick={onExpand}
    >
      <div
        className={`relative w-full flex-1 min-h-0 ${expanded ? "" : "aspect-video"}`}
        onTouchStart={expanded ? onTouchStart : undefined}
        onTouchMove={expanded ? onTouchMove : undefined}
        onTouchEnd={expanded ? onTouchEnd : undefined}
        style={expanded ? { touchAction: "none" } : undefined}
      >
        <div
          className="absolute inset-0"
          style={{
            transform: `translate(${ptz.x}%, ${ptz.y}%) scale(${ptz.zoom})`,
            transformOrigin: "center center",
            transition: "transform 180ms ease-out",
          }}
        >
          {showLive ? (
            <LiveTile ref={tileRef} camera={camera} />
          ) : showRecorded ? (
            <RecordedTile ref={tileRef} camera={camera} footage={footageInfo} onFootageChange={setFootageInfo} />
          ) : showOff ? (
            <CameraOffPanel camera={camera} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="mono text-[10px] text-zinc-600 uppercase tracking-widest">connecting...</div>
            </div>
          )}
        </div>

        {/* Signal-loss glitch overlay — occasional 3-5s "reconnecting" hiccup on live tiles */}
        {glitching && (showRecorded || showLive) && (
          <div
            data-testid={`signal-glitch-${camera.channel}`}
            className="absolute inset-0 z-20 pointer-events-none flex items-center justify-center bg-black/85"
            style={{ backdropFilter: "blur(1.5px)" }}
          >
            <div className="absolute inset-0 scanline opacity-70" />
            <div
              className="absolute inset-0 mix-blend-screen opacity-30"
              style={{
                background:
                  "repeating-linear-gradient(0deg, rgba(255,255,255,0.15) 0px, rgba(255,255,255,0.15) 1px, transparent 1px, transparent 3px)",
                animation: "glitch-flicker 0.15s infinite",
              }}
            />
            <div className="relative text-center">
              <div className="mono text-[10px] uppercase tracking-widest text-red-400 rec-blink mb-1">▸ Signal Lost</div>
              <div className="mono text-[9px] uppercase tracking-widest text-zinc-400">Reconnecting to {channel}...</div>
            </div>
          </div>
        )}

        {/* Overlays */}
        <div className="pointer-events-none absolute inset-0">
          {(showRecorded || showLive) && (
            <div className="absolute top-2 left-2 flex items-center gap-1.5">
              <span className="w-2 h-2 bg-red-500 rec-blink rounded-full" />
              <span className="mono text-[10px] text-red-400 tracking-widest">REC</span>
              {showLive && (
                <span className="mono text-[9px] text-amber-400 tracking-widest ml-1 border border-amber-500/60 px-1">
                  {explicitLive ? "ONVIF" : "AUTO"}
                </span>
              )}
            </div>
          )}

          <div className="absolute top-2 right-2 mono text-[11px] text-white text-right leading-tight"
               style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.9)" }}>
            <div>{dateStr}</div>
            <div className="text-cyan-300">{timeStr} <span className="text-amber-400 text-[9px]">IST</span></div>
          </div>

          <div className="absolute bottom-2 left-2 mono text-[11px] text-white"
               style={{ textShadow: "1px 1px 2px rgba(0,0,0,0.9)" }}>
            <div className="flex items-center gap-2">
              <span className="text-cyan-300 font-bold">{channel}</span>
              <span className="text-zinc-200">{camera.name}</span>
            </div>
            {camera.location && (
              <div className="text-zinc-400 text-[10px]">{camera.location}</div>
            )}
          </div>

          {(showRecorded || showLive) && (
            <div className="absolute bottom-2 right-2 flex items-center gap-1">
              <Radio size={10} className={playbackMode ? "text-amber-400" : "text-emerald-400"} />
              <span className={`mono text-[10px] tracking-widest ${playbackMode ? "text-amber-400" : "text-emerald-400"}`}>
                {playbackMode ? "PLAYBACK" : "LIVE"}
              </span>
            </div>
          )}
        </div>

        {/* Snapshot button — expanded view only, hidden when camera is off */}
        {expanded && !showOff && (
          <button
            data-testid={`snapshot-btn-${camera.channel}`}
            onClick={snapshot}
            className="absolute top-3 right-24 z-10 mono text-[10px] uppercase tracking-widest px-2 py-1.5 bg-black/70 border border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/20 flex items-center gap-1.5"
            title="Save frame as PNG"
          >
            <SnapIcon size={12} /> Snapshot
          </button>
        )}

        {/* Playback speed selector — expanded view + recorded footage + playback mode.
            Docked bottom-center (raised above mobile collapsed-PTZ row on narrow viewports). */}
        {expanded && showRecorded && playbackMode && (
          <div
            data-testid={`speed-panel-${camera.channel}`}
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-24 md:bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 bg-black/70 backdrop-blur-md border border-amber-500/40 px-1.5 py-1 shadow-[0_0_15px_rgba(0,0,0,0.8)]"
          >
            <span className="mono text-[9px] uppercase tracking-widest text-amber-400 pr-1">Speed</span>
            {[0.5, 1, 2, 4].map((s) => (
              <button
                key={s}
                data-testid={`speed-${s}x-${camera.channel}`}
                onClick={() => setSpeed(s)}
                className={`mono text-[10px] uppercase tracking-widest h-6 px-2 border transition-colors ${speed === s ? "bg-amber-500/25 border-amber-500 text-amber-300" : "bg-transparent border-zinc-700 text-zinc-400 hover:border-amber-500/60 hover:text-amber-300"}`}
              >
                {s}x
              </button>
            ))}
          </div>
        )}

        {/* Digital PTZ overlay panel — expanded view only, only when video is showing.
            Collapsible so it doesn't cover the video (especially on mobile).
            Docked to the bottom-right (above the LIVE indicator) to keep center and bottom-left clear. */}
        {expanded && !showOff && (
          <div
            data-testid={`ptz-panel-${camera.channel}`}
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-10 right-3 z-10 bg-black/60 backdrop-blur-md border border-cyan-500/40 p-2.5 select-none shadow-[0_0_15px_rgba(0,0,0,0.8)] rounded-sm group-hover:border-cyan-500/60 group-hover:bg-black/70 transition-all duration-300"
          >
            <div className="mono text-[9px] uppercase tracking-widest text-cyan-400 mb-1.5 flex items-center justify-between gap-3">
              <button
                data-testid={`ptz-toggle-${camera.channel}`}
                onClick={() => setPtzOpen((v) => !v)}
                className="flex items-center gap-1 hover:text-cyan-300"
                title={ptzOpen ? "Hide PTZ" : "Show PTZ"}
              >
                <span>PTZ Control</span>
                {ptzOpen ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
              </button>
              <span className="text-amber-400">×{ptz.zoom.toFixed(2)}</span>
            </div>
            {ptzOpen && (
              <>
                <div className="grid grid-cols-3 gap-1 w-[132px]">
                  <div />
                  <button data-testid={`ptz-up-${camera.channel}`} onClick={() => ptzMove(0, -ptzStep)} disabled={ptz.zoom <= 1} className="border border-zinc-700 h-9 flex items-center justify-center text-zinc-300 hover:bg-cyan-500/20 hover:border-cyan-500 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronUp size={16} /></button>
                  <div />
                  <button data-testid={`ptz-left-${camera.channel}`} onClick={() => ptzMove(-ptzStep, 0)} disabled={ptz.zoom <= 1} className="border border-zinc-700 h-9 flex items-center justify-center text-zinc-300 hover:bg-cyan-500/20 hover:border-cyan-500 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronLeft size={16} /></button>
                  <button data-testid={`ptz-home-${camera.channel}`} onClick={ptzHome} title="Reset view" className="border border-cyan-500/60 h-9 flex items-center justify-center text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/25 mono text-[9px]"><Maximize2 size={12} /></button>
                  <button data-testid={`ptz-right-${camera.channel}`} onClick={() => ptzMove(ptzStep, 0)} disabled={ptz.zoom <= 1} className="border border-zinc-700 h-9 flex items-center justify-center text-zinc-300 hover:bg-cyan-500/20 hover:border-cyan-500 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight size={16} /></button>
                  <div />
                  <button data-testid={`ptz-down-${camera.channel}`} onClick={() => ptzMove(0, ptzStep)} disabled={ptz.zoom <= 1} className="border border-zinc-700 h-9 flex items-center justify-center text-zinc-300 hover:bg-cyan-500/20 hover:border-cyan-500 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronDown size={16} /></button>
                  <div />
                </div>
                <div className="mt-1.5 grid grid-cols-2 gap-1 w-[132px]">
                  <button data-testid={`ptz-zoom-in-${camera.channel}`} onClick={() => ptzZoom(zoomStep)} disabled={ptz.zoom >= 4} className="border border-zinc-700 h-8 flex items-center justify-center text-zinc-300 hover:bg-cyan-500/20 hover:border-cyan-500 disabled:opacity-30 disabled:cursor-not-allowed"><ZoomIn size={13} /></button>
                  <button data-testid={`ptz-zoom-out-${camera.channel}`} onClick={() => ptzZoom(1 / zoomStep)} disabled={ptz.zoom <= 1} className="border border-zinc-700 h-8 flex items-center justify-center text-zinc-300 hover:bg-cyan-500/20 hover:border-cyan-500 disabled:opacity-30 disabled:cursor-not-allowed"><ZoomOut size={13} /></button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Corner brackets */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-0 left-0 w-3 h-3 border-l border-t border-cyan-500/70" />
          <div className="absolute top-0 right-0 w-3 h-3 border-r border-t border-cyan-500/70" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-l border-b border-cyan-500/70" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-r border-b border-cyan-500/70" />
        </div>
      </div>

      {/* 24hr virtual timeline scrubber — expanded + recorded only */}
      {expanded && showRecorded && (
        <TimelineScrubber
          cameraId={camera.id}
          onSeek={seekFraction}
          currentDate={now}
          playbackMode={playbackMode}
          playbackDate={playbackDate}
        />
      )}
    </div>
  );
}

function CameraOffPanel({ camera }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black" data-testid={`camera-off-${camera.channel}`}>
      <div className="scanline absolute inset-0 opacity-40" />
      <PowerOff size={32} className="text-red-500 opacity-70" />
      <div className="mono text-[11px] uppercase tracking-widest text-red-400">
        Video Loss
      </div>
      <div className="mono text-[9px] text-zinc-500 uppercase tracking-widest">
        Verify camera cable & power
      </div>
      {/* fake snow */}
      <div className="absolute inset-0 pointer-events-none opacity-10"
        style={{
          background: "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 3px)",
        }}
      />
    </div>
  );
}

/* Presents a "24hr for today" scrubber. Since we play a looping recording,
 * seeking maps 0..24hr → 0..duration fraction of the current footage.
 * Also supports timeline bookmarks (backend-stored per-camera pins with notes). */
function TimelineScrubber({ cameraId, onSeek, currentDate, playbackMode = false, playbackDate = null }) {
  const [hover, setHover] = useState(null); // 0..1
  const [bookmarks, setBookmarks] = useState([]);
  const [addingAt, setAddingAt] = useState(null); // 0..1 while composing note
  const [noteDraft, setNoteDraft] = useState("");
  const [hoverBookmark, setHoverBookmark] = useState(null);
  const hoverClearTimer = useRef(null);
  const scheduleClearHover = () => {
    if (hoverClearTimer.current) clearTimeout(hoverClearTimer.current);
    hoverClearTimer.current = setTimeout(() => setHoverBookmark(null), 180);
  };
  const cancelClearHover = () => {
    if (hoverClearTimer.current) { clearTimeout(hoverClearTimer.current); hoverClearTimer.current = null; }
  };
  const barRef = useRef(null);

  // If reviewing a past archive day, the ENTIRE 24h is recorded (nowFrac=1).
  // Only when viewing today (live or playback-today) do we mark the future zone.
  const todayIso = istISODateStr(currentDate);
  const pbIso = playbackDate ? istISODateStr(playbackDate) : null;
  const isPastArchive = playbackMode && pbIso && pbIso < todayIso;
  const nowFrac = isPastArchive ? 1 : istDayFraction(currentDate);

  // Load bookmarks for this camera
  useEffect(() => {
    let mounted = true;
    if (!cameraId) return;
    api.get(`/cameras/${cameraId}/bookmarks`).then((r) => {
      if (mounted) setBookmarks(r.data || []);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [cameraId]);

  const onClick = (e) => {
    e.stopPropagation();
    if (addingAt != null) return; // ignore clicks while composing
    const rect = barRef.current.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (frac > nowFrac) {
      toast.error("Cannot scrub into the future");
      return;
    }
    onSeek(frac);
  };
  const onContextMenu = (e) => {
    // Right-click to add a bookmark
    e.preventDefault();
    e.stopPropagation();
    const rect = barRef.current.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (frac > nowFrac) {
      toast.error("Cannot bookmark the future");
      return;
    }
    setAddingAt(frac);
    setNoteDraft("");
  };
  const onMove = (e) => {
    const rect = barRef.current.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setHover(frac);
  };

  const saveBookmark = async () => {
    if (addingAt == null) return;
    try {
      const r = await api.post(`/cameras/${cameraId}/bookmarks`, {
        day_fraction: addingAt,
        note: noteDraft,
      });
      setBookmarks((b) => [...b, r.data].sort((a, z) => a.day_fraction - z.day_fraction));
      setAddingAt(null);
      setNoteDraft("");
      toast.success("Bookmark pinned");
    } catch (e) {
      toast.error("Failed to save bookmark");
    }
  };
  const removeBookmark = async (id) => {
    try {
      await api.delete(`/cameras/${cameraId}/bookmarks/${id}`);
      setBookmarks((b) => b.filter((x) => x.id !== id));
      setHoverBookmark(null);
      toast.success("Bookmark removed");
    } catch { toast.error("Failed to remove"); }
  };
  const seekToBookmark = (bm) => {
    onSeek(bm.day_fraction);
  };

  const bookmarkNowFrac = nowFrac; // shortcut

  const hoverTime = hover == null ? null : (() => {
    const secs = Math.round(hover * 86400);
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${pad(h)}:${pad(m)}`;
  })();
  const hoverIsFuture = hover != null && hover > nowFrac;

  const p = istParts(currentDate);

  const bmTime = (f) => {
    const secs = Math.round(f * 86400);
    return `${pad(Math.floor(secs / 3600))}:${pad(Math.floor((secs % 3600) / 60))}`;
  };

  return (
    <div className="bg-zinc-950 border-t border-zinc-900 px-3 py-2" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mono text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
        <span className="flex items-center gap-2">
          {isPastArchive
            ? `24H Timeline · ${istDateStr(playbackDate)} (IST)`
            : `24H Timeline · Today (IST)`}
          <button
            data-testid={`bookmark-now-${cameraId}`}
            onClick={(e) => { e.stopPropagation(); setAddingAt(bookmarkNowFrac); setNoteDraft(""); }}
            className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/20 flex items-center gap-1"
            title="Pin current moment"
          >
            <Bookmark size={9} /> Bookmark {isPastArchive ? "point" : "now"}
          </button>
        </span>
        <span className={hoverIsFuture ? "text-red-400" : "text-cyan-400"}>
          {hoverTime
            ? (hoverIsFuture ? `Future · ${hoverTime} IST · Not recorded` : `Seek → ${hoverTime} IST`)
            : (isPastArchive
                ? `Archive · full 24h available`
                : `Now · ${pad(p.h)}:${pad(p.m)} IST`)}
        </span>
      </div>
      <div
        ref={barRef}
        data-testid="timeline-scrubber"
        onClick={onClick}
        onContextMenu={onContextMenu}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        className="relative h-6 bg-black border border-zinc-800 cursor-crosshair select-none"
      >
        {/* Future zone */}
        <div
          className="absolute top-0 bottom-0 bg-zinc-950/95 pointer-events-none"
          style={{
            left: `${nowFrac * 100}%`,
            right: 0,
            backgroundImage:
              "repeating-linear-gradient(45deg, rgba(63,63,70,0.6) 0px, rgba(63,63,70,0.6) 2px, transparent 2px, transparent 6px)",
          }}
        />
        {/* hour ticks */}
        {Array.from({ length: 25 }).map((_, i) => (
          <div
            key={i}
            className={`absolute top-0 bottom-0 ${i % 6 === 0 ? "w-px bg-zinc-700" : "w-px bg-zinc-900"}`}
            style={{ left: `${(i / 24) * 100}%` }}
          />
        ))}
        {/* hour labels */}
        {[0, 6, 12, 18, 24].map((h) => (
          <div
            key={h}
            className="absolute -top-3 mono text-[8px] text-zinc-600 -translate-x-1/2"
            style={{ left: `${(h / 24) * 100}%` }}
          >
            {pad(h)}
          </div>
        ))}
        {/* Bookmarks — inverted cyan flags */}
        {bookmarks.map((bm) => (
          <div
            key={bm.id}
            data-testid={`bookmark-marker-${bm.id}`}
            className="absolute top-0 bottom-0 -translate-x-1/2 flex flex-col items-center cursor-pointer z-10"
            style={{ left: `${bm.day_fraction * 100}%` }}
            onClick={(e) => { e.stopPropagation(); seekToBookmark(bm); }}
            onMouseEnter={() => { cancelClearHover(); setHoverBookmark(bm); }}
            onMouseLeave={scheduleClearHover}
            title={bm.note || "Bookmark"}
          >
            <div className="w-px h-full bg-cyan-400/60" />
            <div className="absolute -top-1 w-1.5 h-1.5 bg-cyan-400" />
          </div>
        ))}
        {/* Now marker */}
        <div
          className="absolute top-0 bottom-0 w-px bg-cyan-400 pointer-events-none"
          style={{ left: `${nowFrac * 100}%` }}
        />
        <div
          className="absolute -bottom-0.5 w-2 h-2 bg-cyan-400 rounded-full -translate-x-1/2 pointer-events-none"
          style={{ left: `${nowFrac * 100}%` }}
        />
        {/* Hover marker */}
        {hover != null && (
          <div
            className={`absolute top-0 bottom-0 w-px pointer-events-none ${hoverIsFuture ? "bg-red-500/70" : "bg-amber-400/70"}`}
            style={{ left: `${hover * 100}%` }}
          />
        )}
      </div>
      <div className="mono text-[8px] text-zinc-600 mt-1 flex justify-between">
        <span>Click to seek · Right-click to bookmark ({bookmarks.length} pinned)</span>
        <span>Bookmarks sync across devices for this tenant</span>
      </div>

      {/* Hover tooltip for bookmark */}
      {hoverBookmark && (
        <div
          data-testid="bookmark-tooltip"
          onMouseEnter={cancelClearHover}
          onMouseLeave={scheduleClearHover}
          className="absolute mt-1 -translate-x-1/2 bg-zinc-950 border border-cyan-500/50 px-2 py-1 mono text-[10px] text-cyan-200 pointer-events-auto z-20 flex items-center gap-2"
          style={{ left: `calc(12px + ${hoverBookmark.day_fraction * 100}% * (100% - 24px) / 100%)` }}
        >
          <span className="text-amber-400">{bmTime(hoverBookmark.day_fraction)}</span>
          <span className="text-zinc-100 max-w-[240px] truncate">{hoverBookmark.note || "(no note)"}</span>
          <button
            data-testid={`bookmark-delete-${hoverBookmark.id}`}
            onClick={(e) => { e.stopPropagation(); removeBookmark(hoverBookmark.id); }}
            className="text-red-400 hover:text-red-300 ml-1"
            title="Remove"
          >×</button>
        </div>
      )}

      {/* Compose new bookmark */}
      {addingAt != null && (
        <div
          data-testid="bookmark-compose"
          className="mt-2 border border-cyan-500/40 bg-zinc-950 p-2 flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="mono text-[10px] uppercase tracking-widest text-amber-400 whitespace-nowrap">
            {bmTime(addingAt)} IST
          </span>
          <input
            data-testid="bookmark-note-input"
            autoFocus
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveBookmark(); if (e.key === "Escape") setAddingAt(null); }}
            placeholder="Note (e.g., person entered, vehicle at gate)…"
            className="flex-1 mono text-[11px] bg-black border border-zinc-800 px-2 h-7 text-zinc-100 focus:outline-none focus:border-cyan-500"
            maxLength={120}
          />
          <button
            data-testid="bookmark-save"
            onClick={saveBookmark}
            className="mono text-[10px] uppercase tracking-widest px-2 h-7 border border-cyan-500/60 text-cyan-400 hover:bg-cyan-500/20"
          >Pin</button>
          <button
            data-testid="bookmark-cancel"
            onClick={() => setAddingAt(null)}
            className="mono text-[10px] uppercase tracking-widest px-2 h-7 border border-zinc-800 text-zinc-500 hover:text-red-400"
          >Cancel</button>
        </div>
      )}
    </div>
  );
}
